import { api } from './api'

const basePath = '/sa-online/returns'

/** Unwrap the API envelope to the useful payload (`data` when present). */
const extractPayload = <T = any>(response: any): T => {
  return response?.response?.data || response?.response || response?.data || response
}

// ---------------------------------------------------------------------------
// Types mirroring the SA Online backend (src/app/services/sa-online)
// ---------------------------------------------------------------------------

export type SaSubmissionEnvironment = 'live' | 'test' | 'test-in-live'

export interface GovGatewayCredentials {
  governmentGatewayUserId: string
  governmentGatewayPassword: string
}

export interface SaSubmitPayload extends GovGatewayCredentials {
  saReturnId: string
  environment: SaSubmissionEnvironment
  /** Optional — ignored by the backend today, kept for API parity. */
  validateOnly?: boolean
}

/** `data` field returned by POST /returns/:id/submit */
export interface SaSubmitResult {
  returnId?: string
  correlationId?: string
  status?: 'submitted' | 'processing' | 'accepted' | 'rejected' | 'error'
  hmrcReference?: string
  /** When true, the caller must poll until HMRC returns a final response. */
  requiresPolling?: boolean
  /** HMRC-suggested interval before the next poll, in seconds. */
  pollInterval?: number
}

/** Full envelope of POST /returns/:id/submit */
export interface SaSubmitResponse {
  success: boolean
  message?: string
  errors?: string[]
  data?: SaSubmitResult
}

/** `data` field returned by POST /returns/:id/poll */
export interface SaPollResult {
  status: 'pending' | 'completed' | 'error'
  hmrcReference?: string
  message?: string
  errors?: string[]
  /** HMRC-suggested interval before the next poll, in seconds. */
  pollInterval?: number
}

export const saOnlineService = {
  listReturns: async (): Promise<any> => {
    return api.get<any>(basePath)
  },

  /**
   * GET /returns?status=… — list the user's returns filtered by status/taxYear.
   * Only `status`/`taxYear` are sent (the backend pipe doesn't coerce page/limit).
   * Returns the array of returns (unwrapped).
   */
  listReturnsByStatus: async (params?: { status?: string; taxYear?: string }): Promise<any[]> => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.taxYear) qs.set('taxYear', params.taxYear)
    const query = qs.toString()
    const response = await api.get<any>(query ? `${basePath}?${query}` : basePath)
    const body: any = response?.response ?? response
    return body?.data ?? body?.response?.data ?? []
  },

  /** Convenience: the user's DRAFT returns (replaces the localStorage draft list). */
  listDraftReturns: async (taxYear?: string): Promise<any[]> => {
    return saOnlineService.listReturnsByStatus(taxYear ? { status: 'draft', taxYear } : { status: 'draft' })
  },

  createReturn: async (payload: any): Promise<any> => {
    const response = await api.post<any>(basePath, payload)
    return extractPayload(response)
  },

  updateReturn: async (id: string, payload: any): Promise<any> => {
    const response = await api.put<any>(`${basePath}/${id}`, payload)
    return extractPayload(response)
  },

  getReturn: async (id: string): Promise<any> => {
    const response = await api.get<any>(`${basePath}/${id}`)
    return extractPayload(response)
  },

  deleteReturn: async (id: string): Promise<any> => {
    const response = await api.delete<any>(`${basePath}/${id}`)
    return extractPayload(response)
  },

  /** Pre-fill helper: personal details from the user's most recent SA return. */
  getPreviousPersonalDetails: async (): Promise<any> => {
    const response = await api.get<any>(`${basePath}/personal-details`)
    return extractPayload(response)
  },

  /**
   * GET /returns/:id/calculate-tax — returns the calculation object
   * (incomeBreakdown, incomeTax, nationalInsurance, totalTaxDue, ...).
   */
  calculateTax: async (id: string): Promise<any> => {
    const response = await api.get<any>(`${basePath}/${id}/calculate-tax`)
    return extractPayload(response)
  },

  /**
   * POST /returns/:id/submit — returns the full envelope so the caller can read
   * both `data` (correlationId / requiresPolling / hmrcReference) and `errors`.
   */
  submitReturn: async (
    id: string,
    payload: SaSubmitPayload,
    options?: any,
  ): Promise<SaSubmitResponse> => {
    const response = await api.post<SaSubmitResponse>(`${basePath}/${id}/submit`, payload, options)
    return (response?.response ?? { success: false }) as SaSubmitResponse
  },

  /**
   * POST /returns/:id/poll — checks HMRC for the final result of a submission.
   * Government Gateway credentials are required on every poll (the backend does
   * not persist them). Returns the poll `data` (status / hmrcReference / errors).
   */
  pollSubmission: async (
    id: string,
    credentials: GovGatewayCredentials,
    options?: any,
  ): Promise<SaPollResult> => {
    const response = await api.post<{ success?: boolean; data?: SaPollResult }>(
      `${basePath}/${id}/poll`,
      credentials,
      options,
    )
    const body: any = response?.response ?? {}
    return (body?.data ?? body) as SaPollResult
  },

  downloadReceipt: async (id: string): Promise<Blob> => {
    const result = await api.get<Blob>(`${basePath}/${id}/receipt`, {
      headers: { Accept: 'application/pdf' },
    })
    return result.response
  },
}

export default saOnlineService
